/**
 * Shared client state and inbox polling for the OncoEMR task notification center.
 * @module tasksNotificationContext
 */

import {
  getRbacRoleIdSnapshot,
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
  subscribeToRbacRoleStore,
} from "@risalabs_frontend_org/auth-wrapper";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { OncoemrTaskListItem } from "../../data-model/oncoemrTasksModel";
import { useOncoemrTasksEnabled } from "../../hooks/useOncoemrTasksEnabled";
import TasksNotificationCenterDrawer from "../../pages/worklists/singleOrder/tasks/components/tasksNotificationCenterDrawer";
import { ackOncoemrTaskWithStaleRetry } from "../../pages/worklists/singleOrder/tasks/helpers/ackOncoemrTaskWithStaleRetry";
import { RootState } from "../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { isTasksNotificationDrawerAllowedPathname } from "./isTasksNotificationDrawerAllowedPathname";
import { mapTaskToNotificationItem } from "./mapTaskToNotificationItem";
import type { TasksNotificationCenterItem } from "./tasksNotificationTypes";
import { useOncoemrTasksInboxPoll } from "./useOncoemrTasksInboxPoll";

export type { TasksNotificationCenterItem } from "./tasksNotificationTypes";

/** Value exposed by {@link TasksNotificationContext}. */
export interface TasksNotificationContextValue {
  /** Unread notification rows (`is_updated` only). */
  items: TasksNotificationCenterItem[];
  /** Count of visible unread notifications. */
  unreadCount: number;
  /** Whether the notification drawer is open. */
  isDrawerOpen: boolean;
  /**
   * Opens the notification drawer (task panel).
   * No-ops when the current route is not on the drawer allow-list.
   */
  openDrawer: () => void;
  /** Closes the notification drawer. */
  closeDrawer: () => void;
  /**
   * Acknowledges and removes notification rows (clears `is_updated` on the server).
   *
   * @param ids - `tasks.id` values to acknowledge.
   */
  dismissItems: (ids: string[]) => void;
  /**
   * Merges a successful ack into polled inbox state.
   *
   * @param taskRowId - Acknowledged task primary key.
   * @param version - New row version from the ack response.
   */
  applyTaskAck: (taskRowId: string, version: number) => void;
  /** Triggers an immediate inbox poll. */
  refetch: () => Promise<void>;
  /** True while the first inbox request is in flight. */
  loading: boolean;
}

const TasksNotificationContext = createContext<
  TasksNotificationContextValue | undefined
>(undefined);

/**
 * Provides polled OncoEMR inbox notifications and renders the global drawer.
 *
 * @param props - React children wrapped by the provider.
 * @returns Provider tree with {@link TasksNotificationCenterDrawer}.
 */
export const TasksNotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const isDrawerAllowedOnRoute =
    isTasksNotificationDrawerAllowedPathname(pathname);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist() ?? "";
  const userId = user?.id ?? "";
  const rbacRoleId = useSyncExternalStore(
    subscribeToRbacRoleStore,
    getRbacRoleIdSnapshot,
    () => "",
  );
  const isAdmin = useMemo(
    () => isCurrentUserAdmin() || isCurrentUserSuperAdmin(),
    [rbacRoleId],
  );
  const enabled = useOncoemrTasksEnabled() && Boolean(orgId && userId);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [ackOverrides, setAckOverrides] = useState<
    Record<string, Pick<OncoemrTaskListItem, "is_updated" | "version">>
  >({});

  const { tasks, loading, refetch } = useOncoemrTasksInboxPoll({
    orgId,
    userId,
    isAdmin,
    enabled,
  });

  useEffect(() => {
    setAckOverrides({});
  }, [orgId, userId]);

  const tasksWithAck = useMemo(
    () =>
      tasks.map((task) => {
        const override = ackOverrides[task.id];
        return override ? { ...task, ...override } : task;
      }),
    [tasks, ackOverrides],
  );

  const unreadTasks = useMemo(
    () => tasksWithAck.filter((task) => task.is_updated),
    [tasksWithAck],
  );

  const items = useMemo(
    () => unreadTasks.map(mapTaskToNotificationItem),
    [unreadTasks],
  );

  const openDrawer = useCallback(() => {
    if (!isTasksNotificationDrawerAllowedPathname(pathname)) {
      return;
    }
    setIsDrawerOpen(true);
  }, [pathname]);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  /** Closes the drawer when navigating away from an allow-listed screen. */
  useEffect(() => {
    if (!isDrawerAllowedOnRoute) {
      setIsDrawerOpen(false);
    }
  }, [isDrawerAllowedOnRoute]);

  const applyTaskAck = useCallback((taskRowId: string, version: number) => {
    setAckOverrides((prev) => ({
      ...prev,
      [taskRowId]: { is_updated: false, version },
    }));
  }, []);

  const dismissItems = useCallback(
    (ids: string[]) => {
      if (ids.length === 0 || !orgId) {
        return;
      }

      const tasksToAck = tasksWithAck.filter(
        (task) => ids.includes(task.id) && task.is_updated,
      );

      if (tasksToAck.length === 0) {
        return;
      }

      tasksToAck.forEach((task) => {
        applyTaskAck(task.id, task.version);
      });

      void (async () => {
        for (const task of tasksToAck) {
          try {
            const result = await ackOncoemrTaskWithStaleRetry(
              task.id,
              orgId,
              task.version,
            );
            applyTaskAck(task.id, result.version);
          } catch {
            void refetch();
          }
        }
      })();
    },
    [orgId, tasksWithAck, applyTaskAck, refetch],
  );

  const value = useMemo<TasksNotificationContextValue>(
    () => ({
      items,
      unreadCount: items.length,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      dismissItems,
      applyTaskAck,
      refetch,
      loading,
    }),
    [
      items,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      dismissItems,
      applyTaskAck,
      refetch,
      loading,
    ],
  );

  const drawerPortal =
    enabled && isDrawerAllowedOnRoute && typeof document !== "undefined"
      ? createPortal(
          <TasksNotificationCenterDrawer
            items={items}
            isOpen={isDrawerOpen}
            onClose={closeDrawer}
            dismissItems={dismissItems}
            loading={loading}
          />,
          document.body,
        )
      : null;

  return (
    <TasksNotificationContext.Provider value={value}>
      {children}
      {drawerPortal}
    </TasksNotificationContext.Provider>
  );
};

/**
 * Reads shared notification center state from {@link TasksNotificationProvider}.
 *
 * @returns Notification context value.
 * @throws When used outside {@link TasksNotificationProvider}.
 */
export const useTasksNotification = (): TasksNotificationContextValue => {
  const context = useContext(TasksNotificationContext);
  if (!context) {
    throw new Error(
      "useTasksNotification must be used within a TasksNotificationProvider",
    );
  }
  return context;
};
